import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Package, ChevronRight, ChevronLeft } from 'lucide-react';
import { useTheme } from '../styles/ThemeContext';
export interface ExteriorPackageOption {
  id: string;
  code: string; // e.g. "VFH 1.1"
  thumbnail: string;
  priceModifier?: number;
}
interface ExteriorPackagePaneProps {
  elevationName: string;
  packages: ExteriorPackageOption[];
  selectedPackageId: string | null;
  onSelectPackage: (pkg: ExteriorPackageOption) => void;
  isOpen: boolean;
  onToggle: () => void;
}
const LETTERS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j'];
export function ExteriorPackagePane({
  elevationName,
  packages,
  selectedPackageId,
  onSelectPackage,
  isOpen,
  onToggle
}: ExteriorPackagePaneProps) {
  const {
    isDark
  } = useTheme();
  if (typeof document === 'undefined') return null;
  return createPortal(<AnimatePresence mode="wait" data-id="element-837">
      {!isOpen ?
    // Collapsed re-open tab — same inset edge as the full pane
    <motion.button key="pkg-pane-tab" initial={{
      opacity: 0,
      x: 24
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: 24
    }} transition={{
      duration: 0.25,
      ease: [0.22, 1, 0.36, 1]
    }} onClick={onToggle} className={`fixed right-24 top-1/2 -translate-y-1/2 flex flex-col items-center gap-2 px-2.5 py-4 rounded-2xl border shadow-2xl backdrop-blur-2xl pointer-events-auto transition-colors ${isDark ? 'bg-slate-900/80 border-white/10 text-white hover:border-cyan-500/40' : 'bg-white/85 border-white/40 text-gray-900 hover:border-teal-500/40'}`} style={{
      zIndex: 2147483646
    }} title="Show packages" data-id="element-838">
          <ChevronLeft className={`w-4 h-4 ${isDark ? 'text-white/50' : 'text-gray-400'}`} data-id="element-839" />
          <Package className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-840" />
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{
        writingMode: 'vertical-rl'
      }} data-id="element-841">
            Packages
          </span>
        </motion.button> : <motion.div key="pkg-pane-full" initial={{
      opacity: 0,
      x: 40
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: 40
    }} transition={{
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }} className={`fixed right-24 top-4 w-[220px] max-h-[calc(100vh-2rem)] rounded-2xl border shadow-2xl backdrop-blur-2xl overflow-hidden pointer-events-auto flex flex-col ${isDark ? 'bg-slate-900/80 border-white/10' : 'bg-white/85 border-white/40'}`} style={{
      zIndex: 2147483646
    }} onClick={e => e.stopPropagation()} data-id="element-842">
          {/* Header */}
          <div className={`flex items-start justify-between px-4 py-3.5 border-b flex-shrink-0 ${isDark ? 'border-white/10' : 'border-gray-100'}`} data-id="element-843">
            <div className="flex items-start gap-2 min-w-0" data-id="element-844">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${isDark ? 'bg-cyan-500/15' : 'bg-teal-500/10'}`} data-id="element-845">
                <Package className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-846" />
              </div>
              <div className="min-w-0" data-id="element-847">
                <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-848">
                  Packages
                </h3>
                <p className={`text-[11px] truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`} title={elevationName} data-id="element-849">
                  {elevationName}
                </p>
              </div>
            </div>
            <button onClick={onToggle} title="Collapse" className={`p-1.5 rounded-full transition-colors flex-shrink-0 ${isDark ? 'text-white/40 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`} data-id="element-850">
              <ChevronRight className="w-4 h-4" data-id="element-851" />
            </button>
          </div>

          {/* Package list */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-3 flex flex-col gap-2.5" data-id="element-852">
            {packages.map((pkg, i) => {
          const isSelected = selectedPackageId === pkg.id;
          const letter = LETTERS[i] ?? String(i + 1);
          return <motion.button key={pkg.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.04,
            duration: 0.2
          }} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => onSelectPackage(pkg)} className={`group relative rounded-xl overflow-hidden border-2 transition-all text-left ${isSelected ? isDark ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.3)]' : 'border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.3)]' : isDark ? 'border-white/10 hover:border-white/30' : 'border-gray-200 hover:border-gray-400'}`} data-id="element-853">
                  <div className="relative aspect-[4/3] overflow-hidden bg-gray-200" data-id="element-854">
                    <img src={pkg.thumbnail} alt={pkg.code} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-855" />

                    {/* Letter badge top-left */}
                    <div className={`absolute top-1.5 left-1.5 w-5 h-5 rounded-md flex items-center justify-center text-[11px] font-bold lowercase backdrop-blur-md ${isSelected ? isDark ? 'bg-cyan-500 text-white' : 'bg-teal-600 text-white' : 'bg-black/55 text-white'}`} data-id="element-856">
                      {letter}
                    </div>

                    {/* Check badge top-right when selected */}
                    {isSelected && <div className={`absolute top-1.5 right-1.5 rounded-full p-0.5 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-857">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-858" />
                      </div>}

                    {/* Code label strip at bottom of thumb */}
                    <div className={`absolute bottom-0 inset-x-0 py-1 px-2 backdrop-blur-md ${isDark ? 'bg-black/60' : 'bg-white/80'}`} data-id="element-859">
                      <span className={`block text-[10px] font-bold text-center uppercase tracking-wider ${isSelected ? isDark ? 'text-cyan-300' : 'text-teal-700' : isDark ? 'text-white/90' : 'text-gray-800'}`} data-id="element-860">
                        {pkg.code}
                      </span>
                    </div>
                  </div>

                  {/* Price */}
                  {(pkg.priceModifier ?? 0) > 0 && <div className={`px-2 py-1 ${isDark ? 'bg-slate-800/60' : 'bg-white'}`} data-id="element-861">
                      <span className={`block text-center text-[10px] font-bold ${isDark ? 'text-white/60' : 'text-gray-600'}`} data-id="element-862">
                        +${pkg.priceModifier!.toLocaleString()}
                      </span>
                    </div>}
                </motion.button>;
        })}
          </div>
        </motion.div>}
    </AnimatePresence>, document.body);
}